import { lazy, type ComponentType } from "react";

/** sessionStorage key: the path a failed chunk load already reloaded for, so it never loops. */
export const CHUNK_RELOAD_KEY = "rp:chunk-reload";

/**
 * A chunk that will not load is almost always a tab opened before a deploy: the old hashed files are
 * gone, so the import 404s (or comes back as index.html). Reloading fetches the new index and its
 * chunk names. Reload at most once per path per session; a second failure is a real outage, so it
 * is rethrown for the root error boundary to show a refresh prompt instead of looping.
 *
 * Returns true when it started a reload.
 */
export const reloadOnceForChunkError = (
  storage: Pick<Storage, "getItem" | "setItem"> | null = safeSessionStorage(),
  reload: () => void = () => window.location.reload(),
  path: string = window.location.pathname
): boolean => {
  try {
    if (storage?.getItem(CHUNK_RELOAD_KEY) === path) {
      return false;
    }
    storage?.setItem(CHUNK_RELOAD_KEY, path);
  } catch {
    // Storage blocked: without a guard a reload could loop, so let the boundary handle it.
    return false;
  }

  reload();
  return true;
};

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * `React.lazy` for a module with named exports (the project never default-exports).
 *
 * `lazyNamed(() => import("./features/x/X"), "X")` puts `X` and everything only it imports into a
 * chunk of its own, fetched the first time it renders. Wrap the render in a `Suspense` boundary.
 * A failed load reloads the page once (see `reloadOnceForChunkError`); while that reload is under
 * way the promise never settles, so nothing flashes an error first.
 */
export const lazyNamed = <K extends string, P extends object>(
  load: () => Promise<Record<K, ComponentType<P>>>,
  name: K
) =>
  lazy(() =>
    load().then(
      (module) => ({ default: module[name] }),
      (error: unknown) => {
        if (reloadOnceForChunkError()) {
          return new Promise<never>(() => {});
        }
        throw error;
      }
    )
  );
